import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin } from 'lucide-react'
import OptimizedImage from './OptimizedImage'
import { BsWhatsapp } from 'react-icons/bs'

const Footer = () => {
  const { t } = useTranslation()
  const currentYear = new Date().getFullYear()

  const quickLinks = [
    { href: '#iqac', label: 'IQAC' },
    { href: '#facilities', label: 'Facilities' },
    { href: '#gallery', label: 'Gallery' },
    { href: '#news-events', label: 'News & Event' },
    { href: '#download', label: 'Download' },
    { href: '#nirf', label: 'Nirf' },
    { href: '#gsirf', label: 'Gsirf' },
    { href: '#naac', label: 'NAAC' }
  ]

  const usefulLinks = [
    { href: 'https://www.bknmu.edu.in/', label: 'BKNMU', external: true },
    { href: 'https://www.bknmu.edu.in', label: 'Result of University', external: true },
    { href: 'https://digilocker.gov.in/', label: 'Digilocker', external: true },
    { href: 'https://www.abc.gov.in/', label: 'Abc Id', external: true },
    { href: 'https://www.ugc.ac.in/', label: 'UGC', external: true },
    { href: 'https://www.digitalgujarat.gov.in/', label: 'Digital Gujarat', external: true },
    { href: 'https://ndl.iitkgp.ac.in/', label: 'National Library', external: true },
    { href: 'https://www.baou.edu.in/', label: 'BAOU', external: true },
    { href: 'https://www.ignou.ac.in/', label: 'IGNOU', external: true },
    { href: 'https://www.aicte-india.org/', label: 'AICT', external: true },
    { href: 'https://www.barcouncilofindia.org/', label: 'BCI', external: true },
  ]

  const socialLinks = [
    {
      icon: <Facebook className="w-5 h-5" />,
      href: 'https://www.facebook.com/share/1BPExpnhXF/',
      label: t('social.facebook'),
      color: 'hover:text-blue-600'
    },
    {
      icon: <Instagram className="w-5 h-5" />,
      href: 'https://www.instagram.com/dr.haribhaigodhanicampus?igsh=cmRkZmNqajFvOG5n',
      label: t('social.instagram'),
      color: 'hover:text-pink-600'
    },
    {
      icon: <Youtube className="w-5 h-5" />,
      href: 'https://youtube.com/@dr.haribhaigodhanicampusjnd?si=k-oyVQ7C9wYtMFCH',
      label: t('social.youtube'),
      color: 'hover:text-red-600'
    },
    {
      icon: <BsWhatsapp className="w-5 h-5" />,
      href: 'https://wa.me/919427805272',
      label: 'WhatsApp',
      color: 'hover:text-green-600'
    }
  ]

  return (
    <footer className="footer-brand relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-5 overflow-hidden">
        <div className="absolute top-0 left-0 w-32 sm:w-48 md:w-64 h-32 sm:h-48 md:h-64 bg-white rounded-full blur-3xl -translate-x-16 sm:-translate-x-24 md:-translate-x-32 -translate-y-16 sm:-translate-y-24 md:-translate-y-32"></div>
        <div className="absolute bottom-0 right-0 w-32 sm:w-48 md:w-64 lg:w-96 h-32 sm:h-48 md:h-64 lg:h-96 bg-white rounded-full blur-3xl translate-x-16 sm:translate-x-24 md:translate-x-32 lg:translate-x-48 translate-y-16 sm:translate-y-24 md:translate-y-32 lg:translate-y-48"></div>
        <div className="absolute top-1/2 left-1/3 w-16 sm:w-24 md:w-32 h-16 sm:h-24 md:h-32 bg-white rounded-full blur-2xl"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content - 3 Column Grid with Grouped Links */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 sm:py-12 lg:py-14 xl:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
            
            {/* Column 1: About & Contact */}
            <div className="space-y-4 sm:col-span-2 lg:col-span-1">
              {/* Logo and Title */}
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-white/10 p-1.5 sm:p-2 border border-white/20 flex-shrink-0">
                    <img
                      src="/favicons/favicon-192x192.png" 
                      alt="Dr. Haribhai Godhani Campus Logo" 
                      className="w-full h-full rounded-md object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold font-poppins leading-tight text-white">
                      Dr. Haribhai<br />Godhani Campus
                    </h3>
                  </div>
                </div>
                <p className="text-xs text-gray-300 leading-relaxed">
                  By Shree Sardar Patel Education Trust And Joint Trusts – Junagadh
                </p>
              </div>

              {/* Compact Contact Info */}
              <div className="space-y-2">
                <div className="flex items-start space-x-2.5 p-2 rounded-md border border-white/10 hover:border-white/20 transition-colors">
                  <MapPin className="w-3.5 h-3.5 text-accent-orange flex-shrink-0 mt-0.5" />
                  <span className="text-xs leading-relaxed text-gray-200">
                    B/H. Railway Station, Junagadh 362001
                  </span>
                </div>
                <div className="flex items-center space-x-2.5 p-2 rounded-md border border-white/10 hover:border-white/20 transition-colors">
                  <Phone className="w-3.5 h-3.5 text-accent-orange flex-shrink-0" />
                  <a href="tel:+919427805272" className="text-sm text-gray-200 hover:text-white transition-colors">
                    +91 94278 05272
                  </a>
                </div>
                <div className="flex items-center space-x-2.5 p-2 rounded-md border border-white/10 hover:border-white/20 transition-colors">
                  <Mail className="w-3.5 h-3.5 text-accent-orange flex-shrink-0" />
                  <span className="text-xs text-gray-200 break-all">
                    info@sardarpateleducationtrust.org
                  </span>
                </div>
              </div>
            </div>

            {/* Column 2: Quick Links & Useful Links (Grouped with smaller gap) */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {/* Quick Links */}
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-sm sm:text-base font-bold font-montserrat text-white tracking-tight">Quick Links</h4>
                <ul className="space-y-1.5 sm:space-y-2">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <a 
                        href={link.href}
                        className="text-xs sm:text-sm text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-200 flex items-center group"
                      >
                        <span className="w-1 h-1 rounded-full bg-accent-orange/70 mr-2 group-hover:w-1.5 group-hover:bg-accent-orange transition-all flex-shrink-0"></span>
                        <span className="truncate">{link.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Useful Links */}
              <div className="space-y-3 sm:space-y-4">
                <h4 className="text-sm sm:text-base font-bold font-montserrat text-white tracking-tight">Useful Links</h4>
                <ul className="space-y-1.5 sm:space-y-2">
                  {usefulLinks.map((link, index) => (
                    <li key={index}>
                      <a 
                        href={link.href}
                        className="text-xs sm:text-sm text-gray-300 hover:text-white hover:translate-x-1 transition-all duration-200 flex items-center group"
                        target={link.external ? "_blank" : undefined}
                        rel={link.external ? "noopener noreferrer" : undefined}
                      >
                        <span className="w-1 h-1 rounded-full bg-accent-orange/70 mr-2 group-hover:w-1.5 group-hover:bg-accent-orange transition-all flex-shrink-0"></span>
                        <span className="truncate">{link.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Column 3: Follow Us */}
            <div className="space-y-3 sm:space-y-4 sm:col-span-2 lg:col-span-1">
              <h4 className="text-sm sm:text-base font-bold font-montserrat text-white tracking-tight">{t('footer.followUs')}</h4>
              
              {/* Social Icons - Tighter Grouping */}
              <div className="flex gap-2 sm:gap-2.5">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-white hover:text-primary-dark hover:-translate-y-1 border border-white/15 touch-target"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>

              {/* Description Text */}
              <p className="text-xs text-gray-300 leading-relaxed pt-1 sm:pt-2">
                Stay connected with us for updates and announcements.
              </p>
              
              <div className="flex items-center space-x-2 text-accent-orange text-xs font-medium pt-1">
                <span className="w-2 h-2 rounded-full bg-accent-orange animate-pulse"></span>
                <span>We're online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar with Subtle Top Divider */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-xs sm:text-sm">
              <p className="text-gray-300 order-2 sm:order-1 text-center sm:text-left">
                Copyright {currentYear} DHGC. All rights reserved.
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-400 order-1 sm:order-2 flex-wrap justify-center">
                <span className="hidden xs:inline">Developed and designed by</span>
                <span className="xs:hidden">By</span>
                <a 
                  href="https://avdinovex.ai/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent-orange hover:text-white transition-colors duration-300 font-semibold inline-flex items-center gap-1.5"
                >
                  
AVD Inovex Pvt. Ltd.
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
